// Shared "workday" engine for jobs.js. Every job in this game follows the
// same 50/20/20/10 Math.random() branch shape (neutral / good / bad /
// promotion-check) with the same promotion cap — this factors that shape
// out once so a job definition is a data literal (numbers + flavor text)
// instead of a ~50-line copy-pasted function. See jobs.js for how each job
// uses it; the numbers/text there are transcribed unchanged from the
// original per-job event() bodies, not rebalanced.
import { get } from 'svelte/store';
import { currentPosition, money, rnd, TimeEnum } from './stores.js';
import { applyEffects, STAT_LABELS } from './effects.js';

export const JobCategories = Object.freeze({
    "Unemployed": 0,
    "Construction": 1,
    "Hustle": 2,
    "Dance": 3,
    // "Misc": 4,
    // "Music": 5,
    // "Food": 6,
    // "Police": 7,
    // "Journalism": 8,
    // "Sales": 9
});

export const JobCategoryTimes = (jobCategory) => {
    switch (jobCategory) {
        case 0: return [];
        case 1: return [TimeEnum.morning];
        case 2: return [TimeEnum.evening, TimeEnum.night];
        case 3: return [TimeEnum.evening];
        case 4: return [TimeEnum.morning, TimeEnum.evening];
        case 5: return [TimeEnum.evening];
        case 6: return [TimeEnum.morning, TimeEnum.evening];
        case 7: return [TimeEnum.evening];
        case 8: return [TimeEnum.morning, TimeEnum.evening];
        case 9: return [TimeEnum.morning];
    }
};

export class Job {
    constructor({
                    name = "Undefined action",
                    employer = "ACME",
                    description = "Action description placeholder",
                    category = JobCategories.Misc,
                    background_url = "",
                    yearly_salary = [1, 2, 3],
                    requirements = "None",
                    canApply = () => true,
                    event = () => () => {console.log("Activated undefined job")},
                } = {}
    ) {
        this.name = name;
        this.employer = employer;
        this.description = description;
        this.category = category;
        this.background_url = background_url;
        this.yearly_salary = yearly_salary;
        this.requirements = requirements;
        this.canApply = canApply;
        this.event = event;
    }

    salary() {
        let current_salary = this.yearly_salary[get(currentPosition)];
        let daily_salary = current_salary / 260;
        return rnd(0.9 * daily_salary, 1.1 * daily_salary);
    }

    dailyAvgSalary() {
        let current_salary = this.yearly_salary[get(currentPosition)];
        return current_salary / 260;
    }
}

export const JOB_PROMOTION_CAP = 5;
export const JOB_OUTCOME_BREAKPOINTS = { good: 0.5, bad: 0.7, promotionCheck: 0.9 };

function average(list) {
    return list.reduce((prev, curr) => prev + curr) / list.length;
}

function canApplyFrom(requirements) {
    return () => requirements.every(r => r.op === '>' ? get(r.stat) > r.value : get(r.stat) < r.value);
}

function requirementsTextFrom(requirements) {
    return requirements
        .map(r => `${STAT_LABELS.get(r.stat)} ${r.op} ${Math.round(r.value * 100)}`)
        .join(', ');
}

function runOutcome(outcome) {
    if (!outcome) return '';
    applyEffects(outcome.effects ?? []);
    if (outcome.money) {
        const delta = typeof outcome.money === 'function' ? outcome.money() : outcome.money;
        money.update(n => n + delta);
    }
    return outcome.text;
}

// requirements: [{ stat, op: '>'|'<', value }]
// dailyEffects: effect descriptors (see effects.js), applied unconditionally every event()
// promotion: { stats: [...storesToAverage], threshold }
// outcomes: { neutral, good, bad, onPromotionCheck?, promoted, notPromoted }
//   - neutral/good/bad/promoted/notPromoted: { text, effects?, money?: number | () => number }
//   - onPromotionCheck: effect descriptors applied once whenever the >0.9 branch
//     is reached, BEFORE the promoted/notPromoted split (some jobs apply a
//     shared effect there regardless of outcome; others fold everything into
//     promoted/notPromoted directly and omit this)
export function makeStandardJob({
    name, employer, description, category, background_url, yearly_salary,
    requirements = [], dailyEffects = [], promotion, outcomes,
}) {
    const event = () => {
        const promotion_condition = average(promotion.stats.map(get)) > promotion.threshold;
        applyEffects(dailyEffects);
        const p = Math.random();
        if (p < JOB_OUTCOME_BREAKPOINTS.good) return runOutcome(outcomes.neutral);
        if (p < JOB_OUTCOME_BREAKPOINTS.bad) return runOutcome(outcomes.good);
        if (p < JOB_OUTCOME_BREAKPOINTS.promotionCheck) return runOutcome(outcomes.bad);

        applyEffects(outcomes.onPromotionCheck ?? []);
        if (promotion_condition) {
            currentPosition.update(n => Math.min(n + 1, JOB_PROMOTION_CAP));
            return runOutcome(outcomes.promoted);
        }
        return runOutcome(outcomes.notPromoted);
    };

    return new Job({
        name, employer, description, category, background_url, yearly_salary,
        requirements: requirementsTextFrom(requirements),
        canApply: canApplyFrom(requirements),
        event,
    });
}
